import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import CreditorSelect from 'components/CreditorSelect';
import DebtStatusSelect from 'components/DebtStatusSelect';
import DruhDluhuSelect from 'components/DruhDluhuSelect';
import { useFormik } from 'formik';
import { Button, Form, FormInput } from 'modules';
import { DebtStatusListQuery } from 'queries/DebtStatus/DebtStatusData';
import { Col, Modal, Row } from 'react-bootstrap';
import * as Yup from 'yup';

export interface DebtData {
  jmeno: string | null;
  stav_dluhu: number | null;
  debtStatus: GQL.IDebtStatus | null;
  druh_smlouvy_veritele: string | null;
  vyse_pujcky: number | null;
  zbyva_uhradit: number | null;
}

interface DebtModalProps {
  data?: DebtData;
  onHide: () => void;
  onSubmit: (data: DebtData) => void;
}

const DebtModal = ({
  data = {
    jmeno: null,
    stav_dluhu: null,
    debtStatus: null,
    druh_smlouvy_veritele: null,
    vyse_pujcky: null,
    zbyva_uhradit: null,
  },
  onHide,
  onSubmit,
}: DebtModalProps) => {
  const debtStatusQuery = useQuery(DebtStatusListQuery);

  const validation = Yup.object().shape({
    jmeno: Yup.string().required('Veriteľ je povinný!'),
    stav_dluhu: Yup.string().required('Stav dluh je povinný!'),
    druh_smlouvy_veritele: Yup.string().required('Druh je povinný!'),
    zbyva_uhradit: Yup.number().integer().required('Zostáva uhradiť je povinné!'),
  });

  const formik = useFormik<DebtData>({
    initialValues: data,
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (data: DebtData) => {
      onSubmit({
        jmeno: data.jmeno,
        stav_dluhu: data.stav_dluhu,
        debtStatus: data.debtStatus,
        druh_smlouvy_veritele: data.druh_smlouvy_veritele,
        vyse_pujcky: data.vyse_pujcky,
        zbyva_uhradit: data.zbyva_uhradit,
      });
      formik.resetForm();
      onHide();
    },
  });

  const onChangeDebtStatus = (e: React.ChangeEvent) => {
    const { name, value } = e.target as HTMLInputElement;

    formik.setFieldValue(name, value);

    if (debtStatusQuery?.data?.debtStatusList) {
      const status = debtStatusQuery?.data?.debtStatusList?.find((s: GQL.IDebtStatus) => s.id === parseInt(value));
      if (status) {
        formik.setFieldValue('debtStatus', status);
      }
    }
  };

  const onChangeCreditor = (creditor: GQL.ICreditor) => {
    formik.setFieldValue('jmeno', creditor?.jmeno);
  };

  const onModalKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  };

  return (
    <Modal
      show={true}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      animation={false}
      dialogClassName="modal-90w max-w-800"
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Nový veriteľ</Modal.Title>
        </Modal.Header>
        <Modal.Body onKeyDown={onModalKeyDown}>
          <Row>
            <Col>
              <CreditorSelect
                name="jmeno"
                label="Veriteľ"
                autoFocus={true}
                required={true}
                value={formik.values.jmeno}
                error={formik.errors.jmeno}
                onChange={formik.handleChange}
                onChangeCreditor={onChangeCreditor}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DebtStatusSelect
                name="stav_dluhu"
                label="Stav Dlhu"
                required={true}
                value={formik.values.stav_dluhu}
                error={formik.errors.stav_dluhu}
                onChange={onChangeDebtStatus}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DruhDluhuSelect
                name="druh_smlouvy_veritele"
                label="Druh"
                required={true}
                value={formik.values.druh_smlouvy_veritele}
                error={formik.errors.druh_smlouvy_veritele}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FormInput
                type="number"
                name="zbyva_uhradit"
                label="Zostáva uhradiť"
                required={true}
                value={formik.values.zbyva_uhradit}
                error={formik.errors.zbyva_uhradit}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Zavrieť
          </Button>
          <Button variant="primary" type="submit">
            Uložiť
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DebtModal;
